import UnsavedChangesNotice from '../components/ui/UnsavedChangesNotice';
import PageHeader from '../components/ui/PageHeader';
import { useConfirmation } from '../contexts/ConfirmationContext';
import { userFacingError } from '../utils/userFacingError';
import React, { useState, useEffect, useRef } from 'react';
import { socialMediaService, LatestIssueResponse } from '../services/socialMediaService';
import { SocialMediaBackgroundService } from '../services/socialMediaBackgroundService';
import { StorageService } from '../services/storageService';
import { SocialMediaBackground } from '../types';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import ProfileImageGenerator from '../components/ProfileImageGenerator';
import SharepicGenerator from '../components/SharepicGenerator';
import BannerGenerator from '../components/BannerGenerator';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const SocialMediaPage: React.FC = () => {
  const { hasRole, user } = useAuth();
  const confirm = useConfirmation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [latestIssue, setLatestIssue] = useState<LatestIssueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Profilbild-Hintergrund States
  const [uploading, setUploading] = useState(false);
  const [profileBackgrounds, setProfileBackgrounds] = useState<SocialMediaBackground[]>([]);
  const [activeProfileBackground, setActiveProfileBackground] = useState<SocialMediaBackground | null>(null);
  const [bannerBackgrounds, setBannerBackgrounds] = useState<SocialMediaBackground[]>([]);
  const [activeBannerBackground, setActiveBannerBackground] = useState<SocialMediaBackground | null>(null);
  const [sharepicBackgrounds, setSharepicBackgrounds] = useState<SocialMediaBackground[]>([]);
  const [activeSharepicBackground, setActiveSharepicBackground] = useState<SocialMediaBackground | null>(null);

  useEffect(() => {
    loadLatestIssue();
    if (hasRole(['administrator', 'redakteur'])) {
      loadAllBackgrounds();
    }
  }, [hasRole]);

  const loadLatestIssue = async () => {
    try {
      setLoading(true);
      setError(null);
      const issue = await socialMediaService.getLatestIssue();
      setLatestIssue(issue);
    } catch (err) {
      const errorMessage = userFacingError(err, 'Unbekannter Fehler');
      setError(errorMessage);
      toast.error(`Fehler beim Laden der neuesten Ausgabe: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadLatestIssue();
  };

  // Alle Hintergründe laden
  const loadAllBackgrounds = async () => {
    try {
      console.log('Lade Hintergründe...');
      const [profile, banner, sharepic] = await Promise.all([
        SocialMediaBackgroundService.getBackgroundsByType('profile'),
        SocialMediaBackgroundService.getBackgroundsByType('banner'),
        SocialMediaBackgroundService.getBackgroundsByType('sharepic')
      ]);

      console.log('Geladene Hintergründe:', { profile, banner, sharepic });

      setProfileBackgrounds(profile);
      setBannerBackgrounds(banner);
      setSharepicBackgrounds(sharepic);

      // Aktive Hintergründe setzen
      const activeProfile = profile.find(bg => bg.isActive) || null;
      const activeBanner = banner.find(bg => bg.isActive) || null;
      const activeSharepic = sharepic.find(bg => bg.isActive) || null;

      setActiveProfileBackground(activeProfile);
      setActiveBannerBackground(activeBanner);
      setActiveSharepicBackground(activeSharepic);
    } catch (error) {
      console.error('Fehler beim Laden der Hintergründe:', error);
      console.error('Error details:', error);
      
      // Spezifische Fehlermeldung basierend auf dem Fehlertyp
      if (error instanceof Error) {
        if (error.message.includes('Collection not found') || error.message.includes('Collection')) {
          toast.error('Die Bildvorlagen sind derzeit nicht verfügbar. Bitte kontaktiere die Administration.');
        } else if (error.message.includes('Database not found')) {
          toast.error('Die Bildvorlagen sind derzeit nicht verfügbar. Bitte kontaktiere die Administration.');
        } else {
          toast.error(`Fehler beim Laden der Hintergründe: ${userFacingError(error, 'Bitte versuche es erneut oder kontaktiere die Administration.')}`);
        }
      } else {
        toast.error('Hintergründe konnten nicht geladen werden');
      }
    }
  };

  // Hintergrund Upload
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'banner' | 'sharepic') => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!StorageService.isValidProfileBackgroundFile(file)) {
      toast.error('Ungültige Datei. Nur JPEG, PNG, WebP bis 10MB erlaubt.');
      return;
    }

    uploadBackground(file, type);
  };

  const uploadBackground = async (file: File, type: 'profile' | 'banner' | 'sharepic') => {
    if (!user) {
      toast.error('Bitte melde dich an');
      return;
    }

    try {
      setUploading(true);
      
      // Generiere einen Namen basierend auf Dateiname und Typ
      const name = `${type}-${file.name.replace(/\.[^/.]+$/, '')}-${new Date().toLocaleDateString()}`;
      
      await SocialMediaBackgroundService.createBackground(
        name,
        type,
        file,
        user.id,
        `Hochgeladen am ${new Date().toLocaleString()}`
      );
      
      // Hintergründe neu laden
      await loadAllBackgrounds();
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)}-Hintergrund erfolgreich hochgeladen!`);
    } catch (error) {
      console.error('Upload-Fehler:', error);
      toast.error('Fehler beim Hochladen des Hintergrunds');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = (type: 'profile' | 'banner' | 'sharepic') => {
    fileInputRef.current?.setAttribute('data-type', type);
    fileInputRef.current?.click();
  };

  const activateBackground = async (backgroundId: string) => {
    if (uploading) return;
    setUploading(true);
    try {
      await SocialMediaBackgroundService.activateBackground(backgroundId);
      await loadAllBackgrounds();
      toast.success('Hintergrund aktiviert');
    } catch (error) {
      console.error('Aktivierungs-Fehler:', error);
      toast.error('Hintergrund konnte nicht aktiviert werden');
    } finally { setUploading(false); }
  };

  const deleteBackground = async (backgroundId: string) => {
    if (uploading) return;
    if (!(await confirm({ title: 'Hintergrund löschen?', description: 'Der Hintergrund steht danach nicht mehr für neue Bilder zur Verfügung.', confirmLabel: 'Hintergrund löschen', danger: true }))) {
      return;
    }

    setUploading(true);
    try {
      await SocialMediaBackgroundService.deleteBackground(backgroundId);
      await loadAllBackgrounds();
      toast.success('Hintergrund gelöscht');
    } catch (error) {
      console.error('Lösch-Fehler:', error);
      toast.error('Hintergrund konnte nicht gelöscht werden');
    } finally { setUploading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <PageHeader title={<>Social-Media-Banner & Profilbild</>} description={<>Erstelle Banner und Profilbilder für Social-Media-Kanäle</>} />
        </div>
        
        <div className="px-6 py-8">
          <div className="space-y-6">
            <UnsavedChangesNotice dirty={false} busy={uploading} />
            {/* Neueste Ausgabe Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-blue-900 mb-2">
                    Neueste Ausgabe
                  </h2>
                  {loading ? (
                    <div className="flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2 text-blue-700">Lade neueste Ausgabe...</span>
                    </div>
                  ) : error ? (
                    <div className="text-red-600">
                      <p className="font-medium">Fehler beim Laden</p>
                      <p className="text-sm">{error}</p>
                    </div>
                  ) : latestIssue ? (
                    <div className="text-blue-800">
                      <p className="text-2xl font-bold">{latestIssue.number}</p>
                      <p className="text-sm text-blue-600 mt-1">
                        Ausgabe: {latestIssue.issue}
                      </p>
                    </div>
                  ) : null}
                </div>
                
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg 
                    className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {loading ? 'Lädt...' : 'Aktualisieren'}
                </button>
              </div>
            </div>

            {/* Hintergrund-Verwaltung */}
            {hasRole(['administrator', 'redakteur']) && (
              <div className="space-y-8">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={(e) => {
                    const type = e.target.getAttribute('data-type') as 'profile' | 'banner' | 'sharepic';
                    if (type) {
                      handleFileSelect(e, type);
                    }
                  }}
                  className="hidden"
                />

                {/* Profilbild-Hintergründe */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Profilbild-Hintergründe
                    </h2>
                    <button
                      onClick={() => handleUploadClick('profile')}
                      disabled={uploading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploading ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Lädt hoch...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Hochladen
                        </>
                      )}
                    </button>
                  </div>

                  {activeProfileBackground && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-green-800">
                          Aktiver Hintergrund: {activeProfileBackground.name}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {profileBackgrounds.map((background) => (
                      <div
                        key={background.id}
                        className={`relative group rounded-lg overflow-hidden border-2 ${
                          background.isActive 
                            ? 'border-green-500 ring-2 ring-green-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={StorageService.getImagePreviewUrlFromStorageUrl(background.storageUrl, 320, 180)}
                          alt={background.name}
                          className="w-full h-32 object-contain object-top bg-gray-100"
                          onError={(e) => {
                            const img = e.currentTarget;
                            if (img.dataset.fallback !== 'true') {
                              img.dataset.fallback = 'true';
                              img.src = background.storageUrl;
                            }
                          }}
                        />
                        
                        {/* Status-Indikator */}
                        {background.isActive && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        
                        {/* Aktionen */}
                        <div className="absolute top-2 left-2 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!background.isActive && (
                            <button
                              onClick={() => activateBackground(background.id)}
                              className="bg-blue-500 text-white rounded-full p-1 hover:bg-blue-600"
                              title="Aktivieren"
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => deleteBackground(background.id)}
                            className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            title="Löschen"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Name */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2">
                          <div className="truncate">{background.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Profilbild-Generator */}
                  {activeProfileBackground && (
                    <div className="mt-6">
                      <ProfileImageGenerator
                        backgroundUrl={activeProfileBackground.storageUrl}
                        backgroundName={activeProfileBackground.name}
                      />
                    </div>
                  )}
                </div>

                {/* Banner-Hintergründe */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Banner-Hintergründe
                    </h2>
                    <button
                      onClick={() => handleUploadClick('banner')}
                      disabled={uploading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploading ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Lädt hoch...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Hochladen
                        </>
                      )}
                    </button>
                  </div>

                  {activeBannerBackground && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-green-800">
                          Aktiver Hintergrund: {activeBannerBackground.name}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {bannerBackgrounds.map((background) => (
                      <div
                        key={background.id}
                        className={`relative group rounded-lg overflow-hidden border-2 ${
                          background.isActive 
                            ? 'border-green-500 ring-2 ring-green-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={StorageService.getImagePreviewUrlFromStorageUrl(background.storageUrl, 320, 180)}
                          alt={background.name}
                          className="w-full h-32 object-contain object-top bg-gray-100"
                          onError={(e) => {
                            const img = e.currentTarget;
                            if (img.dataset.fallback !== 'true') {
                              img.dataset.fallback = 'true';
                              img.src = background.storageUrl;
                            }
                          }}
                        />
                        
                        {/* Status-Indikator */}
                        {background.isActive && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        
                        {/* Aktionen */}
                        <div className="absolute top-2 left-2 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!background.isActive && (
                            <button
                              onClick={() => activateBackground(background.id)}
                              className="bg-blue-500 text-white rounded-full p-1 hover:bg-blue-600"
                              title="Aktivieren"
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => deleteBackground(background.id)}
                            className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            title="Löschen"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Name */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2">
                          <div className="truncate">{background.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Banner-Generator */}
                  {activeBannerBackground && (
                    <div className="mt-6">
                      <BannerGenerator
                        backgroundUrl={activeBannerBackground.storageUrl}
                        backgroundName={activeBannerBackground.name}
                      />
                    </div>
                  )}
                </div>

                {/* Sharepic-Hintergründe */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Sharepic-Hintergründe
                    </h2>
                    <button
                      onClick={() => handleUploadClick('sharepic')}
                      disabled={uploading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploading ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Lädt hoch...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Hochladen
                        </>
                      )}
                    </button>
                  </div>

                  {activeSharepicBackground && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-green-800">
                          Aktiver Hintergrund: {activeSharepicBackground.name}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {sharepicBackgrounds.map((background) => (
                      <div
                        key={background.id}
                        className={`relative group rounded-lg overflow-hidden border-2 ${
                          background.isActive 
                            ? 'border-green-500 ring-2 ring-green-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={StorageService.getImagePreviewUrlFromStorageUrl(background.storageUrl, 320, 180)}
                          alt={background.name}
                          className="w-full h-32 object-contain object-top bg-gray-100"
                          onError={(e) => {
                            const img = e.currentTarget;
                            if (img.dataset.fallback !== 'true') {
                              img.dataset.fallback = 'true';
                              img.src = background.storageUrl;
                            }
                          }}
                        />
                        
                        {/* Status-Indikator */}
                        {background.isActive && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        
                        {/* Aktionen */}
                        <div className="absolute top-2 left-2 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!background.isActive && (
                            <button
                              onClick={() => activateBackground(background.id)}
                              className="bg-blue-500 text-white rounded-full p-1 hover:bg-blue-600"
                              title="Aktivieren"
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => deleteBackground(background.id)}
                            className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            title="Löschen"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Name */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2">
                          <div className="truncate">{background.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>

        {/* Sharepic-Generator */}
        {activeSharepicBackground && (
          <div className="mt-6">
            <SharepicGenerator
              backgroundUrl={activeSharepicBackground.storageUrl}
              backgroundName={activeSharepicBackground.name}
            />
          </div>
        )}

                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaPage;
